import { hostBridge } from '@publicala/delfino';
import type {
    EpubR2Locations,
    EpubR2Selection,
    Flashcard,
    FlashcardCreatePayload,
    FlashcardDeletePayload,
    FlashcardListPayload,
    FlashcardReorderPayload,
    FlashcardUpdatePayload,
    Position,
} from '@publicala/delfino';
import {
    DEFAULT_FLASHCARDS_LIST_MOCK,
    createMockFlashcard,
    createMockUpdatedFlashcard,
    isE2EMockActive,
} from '../e2eMockGuard';

/**
 * @R2 Internal R2 location structure from highlightModel (CSS selector-based).
 * Contains the full Readium Locator data for navigation.
 */
interface VolpeR2Location {
    /** CSS selector-based selection data. */
    selection?: EpubR2Selection;
    /** @R2 Resource href from the publication readingOrder. */
    href?: string;
    /** @R2 MIME type of the resource (e.g., "application/xhtml+xml"). */
    type?: string;
    /** @R2 Readium Locator locations object for navigation. */
    locations?: EpubR2Locations;
    [key: string]: unknown;
}

/**
 * Volpe flashcard payload with flat position fields.
 * EPUB positioning is R2-only (CSS selector-based); R1/CFI is not supported.
 * Transforms to FlashcardCreatePayload with nested position before sending to host.
 */
interface VolpeFlashcardPayload {
    uuid: string;
    color: string;
    colorSlug?: string;
    frontText: string;
    backText: string;
    sourceText?: string;
    sourceTextLength?: number;
    aiGenerated?: boolean;
    startPage?: number;
    startPageStartOffset?: number;
    startPageLength?: number;
    endPage?: number;
    endPageLength?: number;
    epubChapter?: string;
    /** @R2 R2 location with CSS selector-based selection (from highlightModel). */
    location?: VolpeR2Location | null;
}

/**
 * Flattens the in-memory R2 location (`{ href, type, locations, selection: {...} }`) into the
 * `epubR2Location` the host stores and lists back: the selection fields hoisted to the top level
 * next to the locator fields. Everything that reads a persisted card expects this flat shape
 * (createR2Locator reads `text` at the top level, reHydrate wraps it back into `selection`), so a
 * card created in this session must snapshot the same shape the list returns after a reload.
 * @param location - The in-memory R2 location, or null/undefined for a card without one
 * @returns The flat epubR2Location, or undefined when the location carries no selection
 */
export function toEpubR2Location(location: VolpeR2Location | null | undefined): EpubR2Selection | undefined {
    if (!location?.selection) {
        return undefined;
    }

    const { selection } = location;

    return {
        startCss: selection.startCss,
        endCss: selection.endCss,
        startElementOffset: selection.startElementOffset,
        startOffset: selection.startOffset,
        endOffset: selection.endOffset,
        chapterCharOffset: selection.chapterCharOffset,
        text: selection.text,
        // A selection made in this session carries the locator fields on the wrapper. A
        // rehydrated highlight carries them inside `selection`, since reHydrate wraps the
        // stored flat shape. Converting the latter into a flashcard must keep them, or the
        // card has no `locations` and goToLocation has nothing to navigate to.
        href: location.href ?? selection.href,
        type: location.type ?? selection.type,
        locations: location.locations ?? selection.locations,
    };
}

/**
 * Flashcards API for Volpe.
 * Provides methods to create, update, delete, and list flashcards via hostBridge.
 */
class FlashcardsAPI {
    /**
     * Creates a flashcard.
     * @param payload - Flashcard data with flat position fields
     * @returns The created flashcard with assigned ID
     */
    async store(payload: VolpeFlashcardPayload): Promise<Flashcard> {
        const position = this.buildPosition(payload);

        if (isE2EMockActive()) {
            return createMockFlashcard(payload, position);
        }

        const createPayload: FlashcardCreatePayload = {
            uuid: payload.uuid,
            color: payload.color,
            colorSlug: payload.colorSlug,
            frontText: payload.frontText,
            backText: payload.backText,
            sourceText: payload.sourceText,
            sourceTextLength: payload.sourceTextLength,
            aiGenerated: payload.aiGenerated,
            position,
        };

        return hostBridge.flashcards.store(createPayload);
    }

    /**
     * Updates an existing flashcard.
     * @param flashcardId - ID of the flashcard to update
     * @param data - Data to update
     * @returns The updated flashcard
     */
    async update(flashcardId: string, data: FlashcardUpdatePayload['data']): Promise<Flashcard> {
        if (isE2EMockActive()) {
            return createMockUpdatedFlashcard(flashcardId, data);
        }

        const updatePayload: FlashcardUpdatePayload = {
            flashcardId,
            data,
        };

        return hostBridge.flashcards.update(updatePayload);
    }

    /**
     * Deletes a flashcard.
     * @param flashcardId - ID of the flashcard to delete
     */
    async delete(flashcardId: string): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }

        const deletePayload: FlashcardDeletePayload = {
            flashcardId,
        };

        return hostBridge.flashcards.delete(deletePayload);
    }

    /**
     * Lists all flashcards.
     * @param issueId - Optional filter by issue ID
     * @returns List of flashcards
     */
    async list(issueId?: string): Promise<Flashcard[]> {
        if (isE2EMockActive()) {
            return DEFAULT_FLASHCARDS_LIST_MOCK;
        }

        const listPayload: FlashcardListPayload = {
            issueId,
        };

        return hostBridge.flashcards.list(listPayload);
    }

    /**
     * Persists the global reading-position order of the user's own flashcards.
     * @param payload - Flashcards with their new sort order (only the entries that changed)
     * @returns Host acknowledgement; a no-op success under E2E mock
     */
    async reorder(payload: FlashcardReorderPayload): Promise<{ success: boolean }> {
        if (isE2EMockActive()) {
            return { success: true };
        }

        return hostBridge.flashcards.reorder(payload);
    }

    /**
     * Builds a position object from flat payload fields.
     * Returns undefined when the flashcard has no position (created from panel without selection).
     * @param payload - Payload with flat position fields
     * @returns Position object or undefined
     */
    private buildPosition(payload: VolpeFlashcardPayload): Position | undefined {
        // @R2 EPUB: CSS selector-based positioning (Readium 2). R1/CFI is not supported.
        const epubR2Location = toEpubR2Location(payload.location);
        if (epubR2Location) {
            return {
                type: 'epub',
                epubChapter: payload.epubChapter,
                epubR2Location,
            };
        }

        if (payload.startPage !== undefined) {
            return {
                type: 'pdf',
                startPage: payload.startPage,
                startPageStartOffset: payload.startPageStartOffset,
                startPageLength: payload.startPageLength,
                endPage: payload.endPage,
                endPageLength: payload.endPageLength,
            };
        }

        return undefined;
    }
}

export const flashcardsAPI = new FlashcardsAPI();
